import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { tipo: "vacaciones", fecha_inicio: "", fecha_fin: "", motivo: "", justificante_url: "", user_id: "", aprobar_al_crear: false };

export function AusenciaDialog({ open, onClose, meta, onSaved }) {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const puedeParaOtro = roles.includes("admin") || roles.includes("gestoria");
  const [form, setForm] = useState(EMPTY);
  const [empleados, setEmpleados] = useState([]);

  useEffect(() => { if (open) setForm(EMPTY); }, [open]);
  useEffect(() => {
    if (!open || !puedeParaOtro) return;
    api.get("/users").then((r) => setEmpleados((r.data || []).filter((u) => u.active !== false))).catch(() => {});
  }, [open, puedeParaOtro]);

  const submit = async () => {
    if (!form.fecha_inicio || !form.fecha_fin) return toast.error("Indica fechas");
    if (form.fecha_fin < form.fecha_inicio) return toast.error("Fecha fin < inicio");
    try {
      const payload = { tipo: form.tipo, fecha_inicio: form.fecha_inicio, fecha_fin: form.fecha_fin, motivo: form.motivo, justificante_url: form.justificante_url || undefined };
      if (puedeParaOtro && form.user_id) { payload.user_id = form.user_id; payload.aprobar_al_crear = !!form.aprobar_al_crear; }
      await api.post("/rrhh/ausencias", payload);
      toast.success(puedeParaOtro && form.user_id ? "Ausencia creada · notificado al empleado" : "Solicitud enviada");
      onSaved && onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al solicitar"); }
  };

  const tipos = (meta?.tipos || []).filter((t) => t !== "compensacion_horas");
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-nueva-ausencia">
        <DialogHeader>
          <DialogTitle>{puedeParaOtro && form.user_id ? "Registrar ausencia de empleado" : "Solicitar ausencia"}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            {puedeParaOtro && form.user_id ? "Como gestor puedes crearla y aprobarla en un solo paso." : "Administración revisará y aprobará/rechazará tu solicitud. No cambia nada hasta entonces."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {puedeParaOtro && (
            <div>
              <Label className="text-xs">Para quién</Label>
              <Select value={form.user_id || "__self__"} onValueChange={(v) => setForm({ ...form, user_id: v === "__self__" ? "" : v, aprobar_al_crear: v === "__self__" ? false : form.aprobar_al_crear })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-target"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-[300px]">
                  <SelectItem value="__self__" data-testid="target-yo">👤 Yo mismo</SelectItem>
                  {empleados.filter((u) => u.id !== user?.id).sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"))
                    .map((u) => <SelectItem key={u.id} value={u.id} data-testid={`target-${u.id}`}>{u.name || u.email}{u.roles?.includes("montador") && " · Montador"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-tipo"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {tipos.map((t) => <SelectItem key={t} value={t}>{meta.labels[t]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Desde</Label>
              <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="form-desde" /></div>
            <div><Label className="text-xs">Hasta</Label>
              <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} className="bg-zinc-900 border-zinc-800" data-testid="form-hasta" /></div>
          </div>
          <div>
            <Label className="text-xs">Motivo / detalles</Label>
            <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Opcional" className="bg-zinc-900 border-zinc-800 min-h-[60px]" data-testid="form-motivo" />
          </div>
          {form.tipo === "baja_medica" && (
            <div>
              <Label className="text-xs flex items-center gap-1"><FileText className="w-3 h-3" /> Justificante (URL)</Label>
              <Input value={form.justificante_url} onChange={(e) => setForm({ ...form, justificante_url: e.target.value })} placeholder="https://…" className="bg-zinc-900 border-zinc-800" data-testid="form-justificante" />
              <div className="text-[10px] text-amber-400 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Obligatorio si la baja supera 3 días</div>
            </div>
          )}
          {puedeParaOtro && form.user_id && (
            <label className="flex items-center gap-2 cursor-pointer p-2 bg-emerald-500/5 border border-emerald-500/20 rounded" data-testid="form-aprobar-al-crear-wrap">
              <input type="checkbox" checked={form.aprobar_al_crear} onChange={(e) => setForm({ ...form, aprobar_al_crear: e.target.checked })} data-testid="form-aprobar-al-crear" className="w-4 h-4 accent-emerald-500" />
              <span className="text-xs"><b className="text-emerald-300">Aprobar en el mismo paso</b></span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={submit} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="form-submit"><Check className="w-4 h-4 mr-2" /> Enviar solicitud</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
